import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button, Container, Modal, ModalHeader, ModalBody, ModalFooter, ListGroup, ListGroupItem } from 'reactstrap';
import { sendVirtualPageView } from '../../../shared/tracking.js';
import useJuryData from '../JuryProjectsList/useJuryData';
import {
    useJuryStore,
    useJuryFundingOptions,
    useJuryPointsConfiguration,
    useJuryVotingType,
} from '@core/frontend/stores';
import JuryProjectsGrid from '../JuryProjectsList/JuryProjectsGrid';
import styles from './JuryFinalVoteSubmission.module.scss';
import { IconCheck } from '@tabler/icons-react';
import { isFundingFullyAssigned, sumPoints } from '@core/business-logic';

function JuryFinalVoteSubmission() {
    useJuryData(); // triggers one-time fetch

    const votingType = useJuryVotingType();
    const pointsConfig = useJuryPointsConfiguration();
    const projects = useJuryStore((s) => s.projects);
    const poll = useJuryStore((s) => s.poll);
    const points = useJuryStore((s) => s.points);
    const fundingOptions = useJuryFundingOptions();
    const isConfirmed = useJuryStore((s) => s.isConfirmed);
    const confirmVotes = useJuryStore((s) => s.confirmVotes);

    const [showModal, setShowModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const isPointsMode = votingType === 'points';

    useEffect(() => {
        sendVirtualPageView('Jury - Abstimmung');
    }, []);

    const assignedCount = Object.keys(poll).length;
    const totalCount = fundingOptions.length;
    const remainingCount = totalCount - assignedCount;
    const allAssigned = isFundingFullyAssigned(assignedCount, totalCount);

    // Projects that have a funding amount assigned, sorted by amount (highest first)
    const fundedProjects = projects.filter((p) => poll[p._id] !== undefined).sort((a, b) => poll[b._id] - poll[a._id]);

    // Projects with a points entry (0 points included), sorted by points (highest first)
    const ratedProjects = projects
        .filter((p) => points[p._id] !== undefined)
        .sort((a, b) => points[b._id] - points[a._id]);

    const ratedCount = ratedProjects.length;
    const projectCount = projects.length;
    const usedPoints = sumPoints(points);
    const isPointsLimitEnabled = pointsConfig.isTotalPointsLimitEnabled && pointsConfig.maxTotalPoints !== null;
    const remainingPoints = isPointsLimitEnabled ? Math.max(0, pointsConfig.maxTotalPoints - usedPoints) : null;
    const allProjectsRated = ratedCount === projectCount && projectCount > 0;
    const mustRateAllProjects = pointsConfig.mustRateAllProjects;
    const canConfirm = isPointsMode ? !mustRateAllProjects || allProjectsRated : allAssigned;

    // Quick lookup: amount → display name (e.g. 5000 → "Hauptpreis")
    const fundingDisplayByAmount = fundingOptions.reduce((acc, opt) => {
        acc[opt.amount] = opt.displayValue;
        return acc;
    }, {});

    const handleConfirm = async () => {
        setIsSubmitting(true);
        setError(null);
        try {
            await confirmVotes();
            setShowModal(false);
        } catch (err) {
            setError('Die Bestätigung ist fehlgeschlagen. Bitte versuchen Sie es erneut.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getRemainingText = () => {
        if (isPointsMode) {
            return remainingPoints !== null
                ? `Es sind noch ${remainingPoints} von ${pointsConfig.maxTotalPoints} Punkten zu vergeben`
                : `${usedPoints} Punkte vergeben`;
        }
        return remainingCount > 0
            ? `Es sind noch ${remainingCount} von ${totalCount} Förderungen zu vergeben`
            : `Alle ${totalCount} Förderungen vergeben`;
    };

    const getSubmitTitle = () => {
        if (canConfirm) return undefined;
        return isPointsMode
            ? `Bitte bewerten Sie noch alle ${projectCount} Teilnehmer bevor Sie bestätigen.`
            : `Bitte vergeben Sie noch alle ${totalCount} Förderungen bevor Sie bestätigen.`;
    };

    const submitLabel = isPointsMode ? 'Punktevergabe bestätigen' : 'Förderungsvergabe bestätigen';
    const confirmedLabel = isPointsMode
        ? 'Ihre Punktevergabe wurde erfolgreich bestätigt.'
        : 'Ihre Förderungsvergabe wurde erfolgreich bestätigt.';

    const listProjects = isPointsMode ? ratedProjects : fundedProjects;

    const SubmitSection = () => (
        <Container className={styles.SubmitSection}>
            <p className={`fw-bold ${styles.RemainingText}`} role="status" aria-live="polite">
                {getRemainingText()}
                {isPointsMode && mustRateAllProjects && (
                    <span className="d-block fw-normal">
                        {ratedCount} von {projectCount} Teilnehmern bewertet
                    </span>
                )}
            </p>

            {isConfirmed ? (
                <p className={styles.ConfirmedText}>
                    <IconCheck aria-hidden="true" /> {confirmedLabel}
                </p>
            ) : (
                <Button
                    color="primary"
                    className={styles.SubmitButton}
                    title={getSubmitTitle()}
                    disabled={!canConfirm}
                    onClick={() => setShowModal(true)}
                >
                    {submitLabel}
                </Button>
            )}
        </Container>
    );

    return (
        <>
            <Helmet>
                <title>Jury-Voting - Abstimmung</title>
            </Helmet>

            <h1 className="visually-hidden">
                {isPointsMode ? 'Jury-Voting Punktevergabe' : 'Jury-Voting Förderungsvergabe'}
            </h1>

            <SubmitSection />

            <JuryProjectsGrid
                projects={listProjects}
                showCount={false}
                showUnratedFilter={!isPointsMode}
                emptyMessage={
                    isPointsMode
                        ? 'Vergeben Sie Punkte über die Punktzahl-Steuerung auf der Teilnehmerkarte.'
                        : 'Weisen Sie Teilnehmern eine Förderung zu, indem Sie das Förderungsmenü auf der Teilnehmerkarte verwenden.'
                }
            />

            <SubmitSection />

            <Modal isOpen={showModal} toggle={() => !isSubmitting && setShowModal(false)}>
                <ModalHeader toggle={() => !isSubmitting && setShowModal(false)}>{submitLabel}</ModalHeader>
                <ModalBody>
                    <p>
                        Möchten Sie Ihre {isPointsMode ? 'Punktevergabe' : 'Förderungsvergabe'} wirklich abschließend
                        bestätigen? Nach der Bestätigung können keine Änderungen mehr vorgenommen werden.
                    </p>
                    <ListGroup flush>
                        {listProjects.map((p) => {
                            if (isPointsMode) {
                                return (
                                    <ListGroupItem key={p._id}>
                                        <strong className={styles.ContestantNameInModal}>{p.contestantName}</strong>
                                        {points[p._id]} Punkte
                                    </ListGroupItem>
                                );
                            }
                            const amount = poll[p._id];
                            const label = fundingDisplayByAmount[amount] ?? `${amount?.toLocaleString('de-DE')} €`;
                            return (
                                <ListGroupItem key={p._id}>
                                    <strong className={styles.ContestantNameInModal}>{p.contestantName}</strong>
                                    {label}
                                </ListGroupItem>
                            );
                        })}
                    </ListGroup>
                    {error && (
                        <p className="text-danger" role="alert">
                            {error}
                        </p>
                    )}
                </ModalBody>
                <ModalFooter className={styles.ModalFooter}>
                    <Button
                        color="primary"
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                        className={styles.ModalButton}
                    >
                        {isSubmitting ? 'Wird bestätigt…' : `Ja, ${submitLabel}`}
                    </Button>
                    <Button
                        color="primary"
                        outline
                        onClick={() => setShowModal(false)}
                        disabled={isSubmitting}
                        className={styles.ModalButton}
                    >
                        Abbrechen
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default JuryFinalVoteSubmission;
